import type { GuideFaq } from "@/components/seo/GuidePage";

const BASE_URL = "https://adkiro.com";
const PUBLISHER_ID = `${BASE_URL}/#organization`;

interface GuidePageSeoInput {
  path: string;
  title: string;
  description: string;
  /** H1 / article headline. Kept separate from the <title> tag, which carries the brand suffix. */
  headline: string;
  /** ISO date the guide was first published. */
  published: string;
  /** ISO date of the last substantive revision. */
  modified: string;
  faqs: GuideFaq[];
}

/**
 * Head + JSON-LD for an explainer guide.
 *
 * Guides carry Article rather than Service: they exist to answer a question,
 * not to sell an engagement, and search and assistant surfaces treat the two
 * very differently. The FAQPage block is what tends to get quoted directly,
 * so each answer is written to stand alone without the surrounding page.
 */
export function guidePageHead({
  path,
  title,
  description,
  headline,
  published,
  modified,
  faqs,
}: GuidePageSeoInput) {
  const pageUrl = `${BASE_URL}${path}`;

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: pageUrl },
      { property: "og:type", content: "article" },
      { property: "og:image", content: `${BASE_URL}/logo.png` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: pageUrl }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${pageUrl}#article`,
              headline,
              description,
              url: pageUrl,
              datePublished: published,
              dateModified: modified,
              inLanguage: "en",
              author: { "@id": `${BASE_URL}/#kristian-kinchev` },
              publisher: { "@id": PUBLISHER_ID },
              isPartOf: { "@id": `${BASE_URL}/#website` },
              mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
              about: { "@type": "Thing", name: "Meta Ads" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Guides", item: `${BASE_URL}/guides` },
                { "@type": "ListItem", position: 3, name: headline, item: pageUrl },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            },
          ],
        }),
      },
    ],
  };
}
